import { t } from "@hakili/i18n";
import { ArrowLeft } from "lucide-react";
import { useState } from "react";

/**
 * `round C5-2`, `M2`, decision D: **« Contrôle empêché », the drawing's form**
 * (`hakili-expert.jsx`, `ControleEmpecheFormulaire`). Not a verdict: a fact,
 * with its reason from the drawing's list (`RAISONS_EMPECHEMENT`), the
 * sentence under « autre », posted to Hakili by the space's door. Who bears
 * a wasted pass is « à trancher », in the drawing's own words; no figure.
 */

export const PREVENTED_REASONS = [
  { id: "absent", label: "expert.empeche.absent", help: "expert.empeche.absentAide" },
  { id: "cles", label: "expert.empeche.cles", help: "expert.empeche.clesAide" },
  { id: "batterie", label: "expert.empeche.batterie", help: "expert.empeche.batterieAide" },
  { id: "inaccessible", label: "expert.empeche.inaccessible", help: "expert.empeche.inaccessibleAide" },
  { id: "ferme", label: "expert.empeche.ferme", help: "expert.empeche.fermeAide" },
  { id: "autre", label: "expert.empeche.autre", help: "expert.empeche.autreAide" },
] as const;

export function ControlPrevented(props: {
  readonly vehicleLabel: string;
  readonly reference: string;
  readonly back: () => void;
  readonly post: (input: { readonly reason: string; readonly detail: string }) => Promise<void>;
}): React.JSX.Element {
  const { vehicleLabel, reference, back, post } = props;
  const [reason, setReason] = useState<string | null>(null);
  const [detail, setDetail] = useState("");
  const [busy, setBusy] = useState(false);
  const [refusal, setRefusal] = useState<string | null>(null);
  const missing =
    reason === null
      ? t("expert.empeche.manqueRaison")
      : reason === "autre" && detail.trim() === ""
        ? t("expert.empeche.manquePhrase")
        : null;

  const send = (): void => {
    if (busy || reason === null || missing !== null) return;
    setBusy(true);
    setRefusal(null);
    void (async () => {
      try {
        await post({ reason, detail: detail.trim() });
      } catch (failure) {
        setRefusal((failure as { message?: string } | null)?.message ?? t("common.error.connexionEchouee"));
      } finally {
        setBusy(false);
      }
    })();
  };

  return (
    <div className="pb-24" data-testid="controle-empeche">
      <div className="flex items-start gap-3 px-5 pb-3 pt-5">
        <button
          type="button"
          onClick={back}
          className="mt-1 text-gray-500"
          aria-label={t("expert.empeche.retour")}
        >
          <ArrowLeft className="h-4 w-4" aria-hidden="true" />
        </button>
        <div className="min-w-0">
          <h1 className="text-lg font-semibold leading-snug tracking-tight">{t("expert.empeche.titre")}</h1>
          <p className="text-xs text-gray-500 tabular-nums">
            {vehicleLabel} · {reference}
          </p>
        </div>
      </div>
      <div className="mb-3 px-5">
        <div className="rounded-2xl border border-gray-200 bg-white px-4 py-3.5">
          <p className="text-xs leading-relaxed text-gray-600">{t("expert.empeche.pasUnVerdict")}</p>
        </div>
      </div>
      <div className="mb-3 px-5">
        <h2 className="mb-2 text-sm font-semibold">{t("expert.empeche.question")}</h2>
        <div className="space-y-2">
          {PREVENTED_REASONS.map((one) => (
            <button
              key={one.id}
              type="button"
              data-reason={one.id}
              data-testid={`empeche-raison-${one.id}`}
              aria-pressed={reason === one.id}
              onClick={() => {
                setReason(one.id);
              }}
              className={`flex w-full items-start gap-2.5 rounded-2xl border px-4 py-3 text-left ${
                reason === one.id ? "border-indigo-400 bg-indigo-50" : "border-gray-200 bg-white"
              }`}
            >
              <span
                className={`mt-0.5 h-4 w-4 shrink-0 rounded-full border-2 ${
                  reason === one.id ? "border-indigo-600 bg-indigo-600" : "border-gray-300"
                }`}
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">{t(one.label)}</span>
                <span className="mt-0.5 block text-xs leading-relaxed text-gray-500">{t(one.help)}</span>
              </span>
            </button>
          ))}
        </div>
      </div>
      <div className="mb-3 px-5">
        <label className="mb-1.5 block text-xs font-medium text-gray-600" htmlFor="empeche-detail">
          {reason === "autre" ? t("expert.empeche.aSavoir") : t("expert.empeche.aSavoirFacultatif")}
        </label>
        <textarea
          id="empeche-detail"
          rows={3}
          value={detail}
          onChange={(event) => {
            setDetail(event.target.value);
          }}
          placeholder={t("expert.empeche.aSavoirAide")}
          data-testid="empeche-detail"
          className="w-full resize-none rounded-xl border border-gray-200 bg-white px-3 py-2.5 text-sm outline-none placeholder:text-gray-300 focus:border-indigo-400"
        />
      </div>
      <div className="px-5">
        <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-3.5">
          <p className="text-xs leading-relaxed text-gray-500">{t("expert.empeche.aTrancher")}</p>
        </div>
      </div>
      <div className="sticky bottom-0 bg-gray-50 px-5 pb-2 pt-3">
        <button
          type="button"
          disabled={missing !== null || busy}
          onClick={send}
          data-testid="empeche-signaler"
          className="w-full rounded-2xl bg-indigo-600 py-3.5 text-sm font-medium text-white disabled:opacity-40"
        >
          {t("expert.empeche.signaler")}
        </button>
        {missing === null ? null : (
          <p className="mt-2 text-center text-xs text-amber-700" data-testid="empeche-manque">
            {missing}
          </p>
        )}
        {refusal === null ? null : (
          <p role="alert" className="mt-2 text-center text-xs text-rose-600" data-testid="empeche-refus">
            {refusal}
          </p>
        )}
      </div>
    </div>
  );
}
